'use client';

import { useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { usePaymentConfig } from '@/tenants/_shared/hooks/usePaymentConfig';
import { useDonationCurrency } from '@/tenants/_shared/hooks/useCurrencies';
import {
  type DonationStrategy,
  type DonationSessionInput,
  type ActivePaymentSession,
} from './types';

export function useEntrantDonationStrategy(
  eventId: string | undefined,
  entrantId: string | undefined,
): DonationStrategy {
  const {
    data: paymentConfig,
    isLoading: isPaymentConfigLoading,
    isFetching: isPaymentConfigFetching,
    isError: isPaymentConfigError,
  } = usePaymentConfig(eventId);

  const currency = useDonationCurrency(eventId);

  const createEntrantSession = trpc.publicDonation.createEntrantSession.useMutation();

  return useMemo(
    (): DonationStrategy => ({
      paymentConfig,
      isPaymentConfigLoading,
      isPaymentConfigFetching,
      isPaymentConfigError,
      currency,
      isFundraisingActive: paymentConfig?.isFundraisingActive ?? true,
      isSessionCreationPending: createEntrantSession.isPending,
      checkoutScope: { scope: 'entrant' as const, eventId: eventId!, entrantId: entrantId! },
      createSession: (input: DonationSessionInput): Promise<ActivePaymentSession> =>
        createEntrantSession.mutateAsync({
          ...input,
          eventId: eventId!,
          entrantId: entrantId!,
        }),
    }),
    [
      paymentConfig,
      isPaymentConfigLoading,
      isPaymentConfigFetching,
      isPaymentConfigError,
      currency,
      createEntrantSession,
      eventId,
      entrantId,
    ],
  );
}
